import { type FC, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  parseISO,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek
} from 'date-fns';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import type { Task } from '../types';
import { useTheme } from '../hooks/useTheme';

interface HeatMapCalendarProps {
  tasks: Task[];
  onDayClick?: (date: Date, tasks: Task[]) => void;
}

const HeatMapCalendar: FC<HeatMapCalendarProps> = ({ tasks, onDayClick }) => {
  const { theme } = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get the start and end of the month view (including leading/trailing days)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  // Get all days to display
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Calculate task density for a given day
  const getTaskDensity = (date: Date) => {
    const dayTasks = tasks.filter(task => {
      if (!task.due_date) return false;
      return isSameDay(parseISO(task.due_date), date);
    });
    return dayTasks.length;
  };

  // Get tasks for a specific day
  const getTasksForDay = (date: Date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      return isSameDay(parseISO(task.due_date), date);
    });
  };

  // Get heat map color based on task count
  const getHeatMapColor = (count: number) => {
    if (count === 0) {
      return theme === 'dark' ? '#1f2937' : '#f3f4f6';
    }
    
    // Define intensity levels
    if (count === 1) {
      return theme === 'dark' 
        ? `rgba(var(--accent-color-rgb), 0.2)`
        : `rgba(var(--accent-color-rgb), 0.15)`;
    } else if (count === 2) {
      return theme === 'dark' 
        ? `rgba(var(--accent-color-rgb), 0.4)`
        : `rgba(var(--accent-color-rgb), 0.3)`;
    } else if (count === 3) {
      return theme === 'dark' 
        ? `rgba(var(--accent-color-rgb), 0.6)`
        : `rgba(var(--accent-color-rgb), 0.5)`;
    } else {
      return theme === 'dark' 
        ? `rgba(var(--accent-color-rgb), 0.8)`
        : `rgba(var(--accent-color-rgb), 0.7)`;
    }
  };

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  // Check if date is today
  const isToday = (date: Date) => {
    return isSameDay(date, new Date());
  };

  // Handle month navigation
  const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  return (
    <div className="space-y-4">
      {/* Month Navigator */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={previousMonth}
          className="p-2 rounded-lg transition-all hover:scale-110"
          style={{
            backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
            color: 'var(--color-theme)'
          }}
        >
          <MdChevronLeft className="w-6 h-6" />
        </button>
        <h3 className="text-xl font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 rounded-lg transition-all hover:scale-110"
          style={{
            backgroundColor: theme === 'dark' ? '#374151' : '#e5e7eb',
            color: 'var(--color-theme)'
          }}
        >
          <MdChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-sm font-semibold py-2"
            style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, index) => {
          const taskCount = getTaskDensity(day);
          const dayTasks = getTasksForDay(day);
          const inCurrentMonth = isCurrentMonth(day);
          const today = isToday(day);

          return (
            <motion.button
              key={index}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onDayClick?.(day, dayTasks)}
              className="relative aspect-square rounded-lg transition-all group"
              style={{
                backgroundColor: getHeatMapColor(taskCount),
                opacity: inCurrentMonth ? 1 : 0.3,
                border: today ? '2px solid var(--color-theme)' : '2px solid transparent'
              }}
              title={`${format(day, 'MMM d, yyyy')} - ${taskCount} task${taskCount !== 1 ? 's' : ''}`}
            >
              {/* Day number */}
              <span 
                className={`text-sm font-semibold ${taskCount > 2 ? 'text-white' : ''}`}
                style={{ 
                  color: taskCount > 2 
                    ? '#ffffff' 
                    : (theme === 'dark' ? '#e5e7eb' : '#374151')
                }}
              >
                {format(day, 'd')}
              </span>

              {/* Task count badge */}
              {taskCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shadow-lg"
                  style={{
                    backgroundColor: 'var(--color-theme)',
                    color: '#ffffff'
                  }}
                >
                  {taskCount}
                </span>
              )}

              {/* Hover tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap"
                style={{
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                  border: '2px solid var(--color-theme)'
                }}
              >
                <p className="text-sm font-semibold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                  {format(day, 'MMM d, yyyy')}
                </p>
                <p className="text-xs" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                  {taskCount} task{taskCount !== 1 ? 's' : ''}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t"
        style={{ borderColor: theme === 'dark' ? '#374151' : '#e5e7eb' }}
      >
        <span className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
          Less
        </span>
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className="w-6 h-6 rounded"
            style={{ backgroundColor: getHeatMapColor(level) }}
            title={`${level} task${level !== 1 ? 's' : ''}`}
          />
        ))}
        <span className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
          More
        </span>
      </div>
    </div>
  );
};

export default HeatMapCalendar;
